import { useState } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Input, Button, useToast, Card, CardBody,
  Table, Thead, Tbody, Tr, Th, Td, IconButton, Flex, Spinner,
} from '@chakra-ui/react';
import { FiTrash2, FiCheck, FiX, FiEdit3 } from 'react-icons/fi';
import { apiService } from '../services/apiService';
import { useBlogCategories } from '../hooks/useBlogCategories';

export default function BlogCategories() {
  const toast = useToast();
  const { categories, loading, refetch } = useBlogCategories();
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [savingId, setSavingId] = useState(null);

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    try {
      setCreating(true);
      await apiService.makeRequest('/blog-categories', {
        method: 'POST',
        body: JSON.stringify({ libelle: newLabel.trim() }),
      });
      toast({ title: 'Catégorie créée', status: 'success', duration: 3000 });
      setNewLabel('');
      refetch();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditLabel(cat.libelle);
  };

  const handleSaveEdit = async (id) => {
    if (!editLabel.trim()) return;
    try {
      setSavingId(id);
      await apiService.makeRequest(`/blog-categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ libelle: editLabel.trim() }),
      });
      toast({ title: 'Catégorie mise à jour', status: 'success', duration: 3000 });
      setEditingId(null);
      refetch();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Supprimer la catégorie "${cat.libelle}" ?`)) return;
    try {
      await apiService.makeRequest(`/blog-categories/${cat.id}`, { method: 'DELETE' });
      toast({ title: 'Catégorie supprimée', status: 'info', duration: 3000 });
      refetch();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    }
  };

  return (
    <Box maxW="700px" mx="auto">
      <Heading size="lg" mb={2}>Catégories du Blog</Heading>
      <Text color="gray.500" mb={6}>Gérez les catégories disponibles lors de la création d'un article.</Text>

      <Card mb={6}>
        <CardBody>
          <HStack>
            <Input
              placeholder="Nouvelle catégorie (ex: Prière, Jeunesse...)"
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              bg="white"
            />
            <Button colorScheme="green" onClick={handleCreate} isLoading={creating} isDisabled={!newLabel.trim()}>
              Ajouter
            </Button>
          </HStack>
        </CardBody>
      </Card>

      {loading ? (
        <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
      ) : (
        <Card>
          <CardBody>
            <Table size="sm">
              <Thead><Tr><Th>Nom</Th><Th></Th></Tr></Thead>
              <Tbody>
                {categories.map(cat => (
                  <Tr key={cat.id}>
                    <Td>
                      {editingId === cat.id ? (
                        <Input
                          size="sm"
                          value={editLabel}
                          onChange={e => setEditLabel(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleSaveEdit(cat.id)}
                          autoFocus
                        />
                      ) : (
                        cat.libelle
                      )}
                    </Td>
                    <Td isNumeric>
                      <HStack justify="flex-end" spacing={1}>
                        {editingId === cat.id ? (
                          <>
                            <IconButton size="sm" icon={<FiCheck />} colorScheme="green" variant="ghost"
                              isLoading={savingId === cat.id} onClick={() => handleSaveEdit(cat.id)} aria-label="Enregistrer" />
                            <IconButton size="sm" icon={<FiX />} variant="ghost"
                              onClick={() => setEditingId(null)} aria-label="Annuler" />
                          </>
                        ) : (
                          <>
                            <IconButton size="sm" icon={<FiEdit3 />} variant="ghost"
                              onClick={() => startEdit(cat)} aria-label="Modifier" />
                            <IconButton size="sm" icon={<FiTrash2 />} variant="ghost" colorScheme="red"
                              onClick={() => handleDelete(cat)} aria-label="Supprimer" />
                          </>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
                {categories.length === 0 && (
                  <Tr><Td colSpan={2}><Text color="gray.500" textAlign="center" py={4}>Aucune catégorie</Text></Td></Tr>
                )}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      )}
    </Box>
  );
}
